import { isEqual, pick, round } from 'lodash';
import { IBookingReceipt } from '../../../data/entities/event/booking';
import { IFXRates } from '../../pages/property/bookings/details/helpers/helpers';
import { DateFormats, getFullDateUtc } from '../date-picker/helpers';

export interface IPendingBookingReceipt extends IBookingReceipt {
	pending?: boolean;
	deleted?: boolean;
}

export const paymentMethods = {
	'Visa/Master Card etc': 'credit card - non-amex',
	'Amex': 'credit card - amex',
	'Channel commission': 'channel commission',
	'FX movement': 'fx movement',
	'Custom receipt': 'custom receipt',
	'Channel payment': 'channel payment',
	'Bank Transfer': 'bank transfer',
	'Manual Mangopay Refund': 'manual mangopay refund',
	'Holidu Payment Fee': 'holidu payment fee',
};

export const displayPaymentMethod = (method: string) => {
	for (const [key, value] of Object.entries(paymentMethods)) {
		if (method === value) {
			return key;
		}
	}

	return method;
};

export const getTotalForCurrency = (bookingReceipts: IPendingBookingReceipt[], currency?: 'booking' | 'owner rent') => {
	const realReceipts = filterDeletedReceipts(bookingReceipts);

	if (currency === 'booking') {
		return realReceipts.reduce(
			(a, b) => a + (b.bookingCurrencyAmount || 0),
			0
		);
	} else if (currency === 'owner rent') {
		return realReceipts.reduce(
			(a, b) => a + (b.ownerRentCurrencyAmount || 0),
			0
		);
	} else {
		return realReceipts.reduce((a, b) => a + (b.amount || 0), 0);
	}
};

/**
 * Calculate the outstanding amount for a booking based on the receipts provided
 * If the outstanding amount is less than 0.01, return 0
 *
 * @param totalValue - the total value of the booking
 * @param bookingReceipts - the receipts for the booking
 * @param currency - the currency to calculate the outstanding amount for
 * @returns the outstanding amount
 */
export const calculateOutstanding = (totalValue: number, bookingReceipts: IBookingReceipt[], currency?: 'booking' | 'owner rent'): number => {
	const outstandingAmount = round(totalValue - getTotalForCurrency(bookingReceipts, currency), 4);
	return Math.abs(outstandingAmount) >= 0.01 ? outstandingAmount : 0;
};

export const calculateConvertedReceipt = (receipt: IBookingReceipt, ownerRentCurrency: string, fxRates: IFXRates) => {
	const fx = fxRates[getFullDateUtc(receipt.date, DateFormats.YearMonthDay)];
	const rate = fx ? (fx.from === receipt.currency && fx.to === ownerRentCurrency ? fx.fxRate : 0) : 0;
	return receipt.amount * rate;
};

export const showFXAdjustment = (
	receipts: IBookingReceipt[], guestPaymentValueInBookingCurrency: number, mismatchedCurrencies: boolean, suggestedFXAdjustment: number
	) => {
	const filteredReceipts = filterDeletedReceipts(receipts);

	const remainingBookingCurrency = Number(calculateOutstanding(guestPaymentValueInBookingCurrency, filteredReceipts, 'booking').toFixed(2));

	if (mismatchedCurrencies && remainingBookingCurrency === 0 && suggestedFXAdjustment !== 0) {
		return filteredReceipts.filter((r) => r.method === 'fx movement').length === 0;
	}
	return false;
};

export const determineCurrency = (method: string, ownerRentCurrency: string, bookingCurrency: string) => {
	if (['fx movement', 'custom receipt'].includes(method))
		return ownerRentCurrency;
	return bookingCurrency;
};

export const compareBookingReceipts = (a: IBookingReceipt, b: IBookingReceipt) => {
	const whitelist = ['method', 'amount', 'currency', 'date', 'reference'];

	return isEqual(
		pick(a, whitelist),
		pick(b, whitelist)
	);
};

export const filterDeletedReceipts = (receipts: IPendingBookingReceipt[]) => receipts.filter((receipt: IPendingBookingReceipt) => !receipt.deleted);

export const getNextWednesday = () => {
	// Get the next wednesday, returns today if today is a wednesday.
	// useful for owner payments as we pay on wednesdays
	const nextWednesday = new Date();
	nextWednesday.setDate(nextWednesday.getDate() + ((7 - nextWednesday.getDay()) % 7 + 3) % 7);
	return nextWednesday;
};
